import React, { useCallback, useEffect, useState } from "react";
import { UserCheck, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, hasWrite } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const t = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");
const dur = (m) => (m === null || m === undefined ? "—" : m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`);
const STATE = { out: ["OUT NOW", "bg-destructive/15 text-destructive"], waiting: ["NOT LEFT YET", "bg-amber-100 text-amber-800"], back: ["BACK", "bg-[hsl(var(--positive))]/15 text-[hsl(var(--positive))]"] };
const COLS = "grid grid-cols-2 md:grid-cols-[110px_minmax(0,1fr)_130px_minmax(0,1.2fr)_90px_110px_120px] gap-2 md:gap-4 px-4 md:px-5 items-center";

const Card = ({ k, label, n }) => (
  <div data-testid={`emp-out-count-${k}`} className={`rounded-lg border px-4 py-3 ${k === "out" && n ? "border-destructive/40 bg-destructive/10" : "border-border bg-card"}`}>
    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="num text-2xl font-bold">{n}</div>
  </div>
);

const Row = ({ r, canWrite, onBack }) => {
  const [l, cls] = STATE[r.state];
  return (
    <div data-testid={`emp-out-row-${r.id}`} className={`${COLS} py-3 text-sm`}>
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded w-fit ${cls}`}>{l}</span>
      <div className="min-w-0"><div className="font-semibold break-words">{r.employee_name}</div><div className="num text-[11px] text-muted-foreground">{r.number} · by {r.created_by_name}</div></div>
      <span className="num text-xs">{r.vehicle_number ? `${r.vehicle_number}${r.ow_vehicle === "employee" ? " (own)" : ""}` : "No vehicle"}</span>
      <span className="text-xs text-muted-foreground break-words col-span-2 md:col-span-1">{r.remarks}</span>
      <span className="num text-xs">Out {t(r.out_at)}{r.returned_at ? <><br />Back {t(r.returned_at)}</> : null}</span>
      <span data-testid={`emp-out-dur-${r.id}`} className={`num text-sm font-bold flex items-center gap-1 ${r.state === "out" && r.minutes >= 120 ? "text-destructive" : ""}`}><Clock size={13} /> {dur(r.minutes)}</span>
      {canWrite && r.state === "out" ? <button data-testid={`emp-back-${r.id}`} onClick={() => onBack(r)} className="h-8 px-2 rounded-md border border-input text-xs font-medium flex items-center gap-1 hover:bg-muted transition-colors"><UserCheck size={13} /> Mark back</button> : <span />}
    </div>
  );
};

export const EmployeesOut = () => {
  const { user } = useAuth();
  const [d, setD] = useState({ rows: [], out: 0, waiting: 0, back: 0 });
  const load = useCallback(() => api.get("/gatepass/employees-out").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);
  const back = async (r) => {
    try { await api.post(`/gatepass/${r.id}/returned`); toast.success(`${r.employee_name} marked back`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <section data-testid="employees-out" className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Card k="out" label="Out now" n={d.out} /><Card k="waiting" label="Not left yet" n={d.waiting} /><Card k="back" label="Back today" n={d.back} />
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Outside work and employee gate passes. Time counts from when the pass was scanned out; it turns red after 2 hours.</p>
        <button data-testid="emp-out-refresh" onClick={load} className="h-9 px-3 rounded-md border border-input text-xs flex items-center gap-1.5 hover:bg-muted transition-colors"><RefreshCw size={13} /> Refresh</button>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className={`${COLS} hidden md:grid py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground`}>
          <span>Status</span><span>Employee</span><span>Vehicle</span><span>Remark</span><span>Time</span><span>Away for</span><span></span>
        </div>
        {d.rows.length === 0 ? <div data-testid="emp-out-empty" className="p-10 text-center text-sm text-muted-foreground">No employees are out right now.</div>
          : <div className="divide-y divide-border">{d.rows.map((r) => <Row key={r.id} r={r} canWrite={hasWrite(user)} onBack={back} />)}</div>}
      </div>
    </section>
  );
};
